import PropTypes from 'prop-types';
import { isBlank, isPresent } from 'utils/helperMethods';
import { head, equals, pickBy, is, isNil, find, propEq, pathOr } from 'ramda';
import { camelizeString } from 'utils/keysConverter';
import Presenter from 'utils/PropTypesPresenter';
import AutoFollowupsMessagePresenter from 'presenters/AutoFollowupsMessagePresenter';
import i18n from 'i18next';

export const WEEKLY_MAX_CONNECTIONS_OPTIONS = [
  { label: 'No Max', value: 'no_max' },
  { label: '5 Connections', value: 5 },
  { label: '10 Connections', value: 10 },
  { label: '20 Connections', value: 20 },
];

export const DAILY_MAX_CONNECTIONS_OPTIONS = [
  { label: 'No Max', value: 'no_max' },
  { label: '2 Connections', value: 2 },
  { label: '4 Connections', value: 4 },
  { label: '8 Connections', value: 8 },
];

const USER_TYPES = {
  buyer: 'buyer',
  loanOfficer: 'loan_officer',
  realEstateAgent: 'real_estate_agent',
  administrator: 'administrator',
};

export default new Presenter(
  {
    id: PropTypes.number,
    userType: PropTypes.string,
    profileImageUrl: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    slug: PropTypes.string,
    verificationStatus: PropTypes.string,
    verificationStatusReason: PropTypes.string,
    profileVideoUrl: PropTypes.string,
    phoneNumber: PropTypes.string,
    email: PropTypes.string,
    emailConfirmed: PropTypes.bool,
    applicationUrl: PropTypes.string,
    nmls: PropTypes.number,
    mls: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mortgageCompany: PropTypes.string,
    companyLogoUrl: PropTypes.string,
    companyNmls: PropTypes.number,
    stateLicenseNumber: PropTypes.string,
    mortgageBidDisclosure: PropTypes.string,
    aboutMe: PropTypes.string,
    address: PropTypes.shape(),
    company: PropTypes.string,
    keepUpdated: PropTypes.bool,
    offerings: PropTypes.arrayOf(PropTypes.string),
    licensedStates: PropTypes.arrayOf(PropTypes.string),
    dailyMaxConnectionsCount: PropTypes.number,
    weeklyMaxConnectionsCount: PropTypes.number,
    disableAllConnections: PropTypes.bool,
    enabledAutoFollowupsMessages: PropTypes.bool,
    autoFollowupsMessages: PropTypes.arrayOf(AutoFollowupsMessagePresenter.shape()),
    provider: PropTypes.string,
  },
  {
    isEmailProvider(user) {
      return this.provider(user) === 'email';
    },
    isBuyer(user) {
      return this.userType(user) === USER_TYPES.buyer;
    },
    isLoanOfficer(user) {
      return this.userType(user) === USER_TYPES.loanOfficer;
    },
    isAdmin(user) {
      return this.userType(user) === USER_TYPES.administrator;
    },
    isRealEstateAgent(user) {
      return this.userType(user) === USER_TYPES.realEstateAgent;
    },
    initials(user) {
      if (this.firstName(user) && this.lastName(user)) {
        return `${head(this.firstName(user))}${head(this.lastName(user))}`;
      }
      const [first, last] = this.slug(user).split(' ');
      return `${head(first)}${head(last)}`;
    },
    formatUserType(user) {
      return camelizeString(this.userType(user));
    },
    fullName(user) {
      if (this.firstName(user) && this.lastName(user)) {
        return `${this.firstName(user)} ${this.lastName(user)}`;
      }
      const [first, last] = this.slug(user).split(' ');
      return `${first} ${last}`;
    },
    getUserType(user) {
      if (this.isBuyer(user)) {
        return i18n.t(`models.App.userTypes.buyer`);
      }
      if (this.isAdmin(user)) {
        return i18n.t(`models.App.userTypes.admin`);
      }
      if (this.isRealEstateAgent(user)) {
        return i18n.t(`models.App.userTypes.realEstateAgent`);
      }
      return i18n.t(`models.App.userTypes.loanOfficer`);
    },
    loanOfficerProfileFields(user) {
      const fields = [
        'verificationStatus',
        'verificationStatusReason',
        'firstName',
        'lastName',
        'profileVideoUrl',
        'phoneNumber',
        'email',
        'applicationUrl',
        'nmls',
        'mortgageCompany',
        'companyLogoUrl',
        'companyNmls',
        'stateLicenseNumber',
        'mortgageBidDisclosure',
        'aboutMe',
        'address',
        'profileImageUrl',
        'keepUpdated',
        'offerings',
      ];
      const data = fields.reduce((acc, item) => ({ ...acc, [item]: this[item](user) }), {});
      return data;
    },
    getValuesByFields(user, fields) {
      const data = fields.reduce((acc, item) => ({ ...acc, [item]: this[item](user) }), {});
      return data;
    },
    buildUserUpdateAttributes(user, values) {
      const keepUpdatedOptions = {
        agree: true,
        disagree: false,
      };
      const { keepUpdated } = values;
      const preparedValues = !isNil(keepUpdated) ? { ...values, keepUpdated: keepUpdatedOptions[keepUpdated] } : values;
      const attr = pickBy((val, key) => !equals(user[key], val), preparedValues);
      return attr;
    },
    bidDisclosure(user) {
      const disclosure = this.mortgageBidDisclosure(user);
      return is(String, disclosure) ? disclosure : '';
    },
    autoFollowupsMessage(user, order) {
      const autoFollowupsMesages = this.autoFollowupsMessages(user);
      if (isBlank(autoFollowupsMesages)) return null;

      return find(propEq('order', order), autoFollowupsMesages);
    },
    autoFollowupsMessageText(user, order) {
      const autoFollowupsMessage = this.autoFollowupsMessage(user, order);
      return isPresent(autoFollowupsMessage) ? AutoFollowupsMessagePresenter.message(autoFollowupsMessage) : null;
    },
    city(user) {
      const address = this.address(user);
      return pathOr(null, ['city'], address);
    },
    state(user) {
      const address = this.address(user);
      return pathOr(null, ['state'], address);
    },
    lat(user) {
      const address = this.address(user);
      return pathOr(null, ['lat'], address);
    },
    lng(user) {
      const address = this.address(user);
      return pathOr(null, ['lng'], address);
    },
    zipCode(user) {
      const address = this.address(user);
      return pathOr(null, ['zipCode'], address);
    },
    streetAddress1(user) {
      const address = this.address(user);
      return pathOr(null, ['streetAddress1'], address);
    },
    streetAddress2(user) {
      const address = this.address(user);
      return pathOr(null, ['streetAddress2'], address);
    },
  },
);



// WEBPACK FOOTER //
// ./src/presenters/UserPresenter.js